import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TablePagination,
    TextField,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Typography,
    InputAdornment,
    IconButton,
    Tooltip
} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../../Services/ServiceLocator";
import { v4 as uuid } from 'uuid';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPencilAlt, faChevronUp, faChevronDown, faTrash, faPlus, faSave, faTimes, faSearch, faStore, faCreditCard} from "@fortawesome/free-solid-svg-icons";
import { AuthenticationService } from "../../../Services/AuthenticationService";
import { filterPersistenceService } from "../../../Services/FilterPersistenceService";
import { getNumberInputDisplayValue, parseNumberInputValue } from '../../../utils/numberInputUtils';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    filterToggle: {
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '2px 10px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        minWidth: '150px',
        '& .MuiCheckbox-root': {
            padding: '0px',
            color: '#35d2d9',
            '&.Mui-checked': {
                color: '#35d2d9',
            },
        }
    },
    filterLabel: {
        marginLeft: '8px',
        fontSize: '14px',
        color: 'rgba(0, 0, 0, 0.87)',
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    toolbarRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    sortableHeader: {
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background-color 0.2s ease',
    },
    editableCell: {
        padding: '8px',
        minWidth: '150px'
    },
    editableInput: {
        width: '100%'
    },
    actionButtons: {
        display: 'flex',
        gap: '8px'
    }
});

class Vendors extends React.Component {
    state = {
        loading: true,
        user: {},
        vendors: [],
        paymentTerms: [],
        page: 0,
        rowsPerPage: 100,
        searchTerm: '',
        sortBy: 'name',
        sortDirection: 'asc',
        activeTab: 0,
        
        // Bulk Edit States
        showVendorBulkEdit: false,
        pendingVendors: [],
        showPaymentTermsBulkEdit: false,
        pendingPaymentTerms: [],
        savingNewValue: false
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    componentDidMount() {
        this.loadSavedFilterSettings();
        this.loadData();
    }

    loadSavedFilterSettings = () => {
        const savedSettings = filterPersistenceService.loadFilterSettings('vendors');
        if (savedSettings) {
            this.setState({
                page: savedSettings.page || 0,
                rowsPerPage: savedSettings.rowsPerPage || 100,
                searchTerm: savedSettings.searchTerm || '',
                sortBy: savedSettings.sortBy || 'name',
                sortDirection: savedSettings.sortDirection || 'asc'
            });
        }
    }

    saveFilterSettings = () => {
        const settings = {
            page: this.state.page,
            rowsPerPage: this.state.rowsPerPage,
            searchTerm: this.state.searchTerm,
            sortBy: this.state.sortBy,
            sortDirection: this.state.sortDirection
        };
        filterPersistenceService.saveFilterSettings('vendors', settings);
    }

    handleChangePage = (event, newPage) => {
        this.setState({page: newPage}, () => {
            this.saveFilterSettings();
        });
    };

    handleChangeRowsPerPage = (event) => {
        this.setState({
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }, () => {
            this.saveFilterSettings();
        });
    };

    loadData = async () => {
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('EditAssembliesAndParts')) {
            globalProps.history.push('/admin/parts');
            return;
        }

        try {
            const vendorService = ServiceLocator.Instance.getVendorService();
            const [vendors, paymentTerms] = await Promise.all([
                vendorService.getAllVendors(),
                vendorService.getAllPaymentTerms()
            ]);

            this.setState({
                loading: false,
                user,
                vendors,
                paymentTerms: paymentTerms.filter(pt => pt.isActive)
            });
        } catch (error) {
            console.error('Error loading data:', error);
            this.setState({ loading: false });
        }
    };

    searchVendor = (event) => {
        this.setState({ searchTerm: event.target.value, page: 0 }, () => {
            this.saveFilterSettings();
        });
    };

    handleSort = (field) => {
        const { sortBy, sortDirection } = this.state;
        const newDirection = sortBy === field && sortDirection === 'asc' ? 'desc' : 'asc';
        this.setState({ sortBy: field, sortDirection: newDirection }, () => {
            this.saveFilterSettings();
        });
    };


    handleDeleteVendor = async (vendor) => {
        if (!window.confirm(`Are you sure you want to delete vendor "${vendor.name}"?`)) {
            return;
        }

        try {
            const vendorService = ServiceLocator.Instance.getVendorService();
            await vendorService.deleteVendor(vendor.id);
            
            this.setState(prevState => ({
                vendors: prevState.vendors.filter(v => v.id !== vendor.id)
            }));
        } catch (error) {
            console.error('Error deleting vendor:', error);
            window.$alertError('Error deleting vendor: ' + error.message);
        }
    };



    handleVendorBulkFieldChange = (vendorId, field, value) => {
        this.setState(prevState => ({
            vendors: prevState.vendors.map(vendor => 
                vendor.id === vendorId 
                    ? { ...vendor, [field]: value }
                    : vendor
            )
        }));
    };


    handleDeletePaymentTerms = async (paymentTerms) => {
        if (!window.confirm(`Are you sure you want to deactivate payment terms "${paymentTerms.name}"?`)) {
            return;
        }

        try {
            const vendorService = ServiceLocator.Instance.getVendorService();
            const updatedPaymentTerms = {
                ...paymentTerms,
                isActive: false
            };
            const savedPaymentTerms = await vendorService.updatePaymentTerms(updatedPaymentTerms);
            
            this.setState(prevState => ({
                paymentTerms: prevState.paymentTerms.filter(pt => pt.id !== paymentTerms.id)
            }));
        } catch (error) {
            console.error('Error deactivating payment terms:', error);
            window.$alertError('Error deactivating payment terms: ' + error.message);
        }
    };

    handlePaymentTermsBulkFieldChange = (paymentTermsId, field, value) => {
        this.setState(prevState => ({
            paymentTerms: prevState.paymentTerms.map(pt => 
                pt.id === paymentTermsId 
                    ? { ...pt, [field]: value }
                    : pt
            )
        }));
    };

    handleTabChange = () => {
        this.setState(prevState => ({ activeTab: prevState.activeTab === 0 ? 1 : 0 }));
    };

    // Vendor Bulk Edit Methods
    addVendorBulkLine = () => {
        const newLine = {
            id: uuid(),
            name: '',
            website: '',
            phoneNumber: '',
            address: '',
            primaryContact: '',
            primaryEmail: '',
            secondaryContact: '',
            secondaryEmail: '',
            paymentTermsId: null,
            isComplete: false
        };
        
        this.setState(prevState => ({
            pendingVendors: [...prevState.pendingVendors, newLine]
        }));
    };

    handleVendorBulkChange = (lineId, field, value) => {
        this.setState(prevState => ({
            pendingVendors: prevState.pendingVendors.map(line => 
                line.id === lineId 
                    ? { ...line, [field]: value, isComplete: !!(line.name && line.name.trim()) }
                    : line
            )
        }));
    };

    removeVendorBulkLine = (lineId) => {
        this.setState(prevState => ({
            pendingVendors: prevState.pendingVendors.filter(line => line.id !== lineId)
        }));
    };

    handleVendorBulkKeyDown = (lineId, event) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            // Get the current field name from the input element
            const currentField = event.target.name;
            
            // Define the field order for tabbing
            const fieldOrder = ['name', 'website', 'paymentTermsId', 'phoneNumber', 'address', 'primaryContact', 'primaryEmail', 'secondaryContact', 'secondaryEmail'];
            const currentFieldIndex = fieldOrder.indexOf(currentField);
            
            // If we're on the last field, go to the name field of the next line (or create one)
            if (currentFieldIndex === fieldOrder.length - 1) {
                event.preventDefault();
                
                // Find current line index
                const currentLineIndex = this.state.pendingVendors.findIndex(line => line.id === lineId);
                
                // If there's a next line, focus its name field
                if (currentLineIndex < this.state.pendingVendors.length - 1) {
                    const nextLineId = this.state.pendingVendors[currentLineIndex + 1].id;
                    
                    setTimeout(() => {
                        const nameField = document.querySelector(`[data-vendor-line-id="${nextLineId}"] input[name="name"]`);
                        if (nameField) {
                            nameField.focus();
                            nameField.select();
                        }
                    }, 100);
                } else {
                    // No next line exists, create one and focus its name field
                    this.addVendorBulkLine();
                    
                    setTimeout(() => {
                        const newLineId = this.state.pendingVendors[this.state.pendingVendors.length - 1]?.id;
                        if (newLineId) {
                            const nameField = document.querySelector(`[data-vendor-line-id="${newLineId}"] input[name="name"]`);
                            if (nameField) {
                                nameField.focus();
                                nameField.select();
                            }
                        }
                    }, 100);
                }
            }
            // Otherwise, let the default tab behavior handle moving to the next field
        }
    };

    saveAllVendorBulkChanges = async () => {
        const self = this;
        const vendorService = ServiceLocator.Instance.getVendorService();
        
        self.setState({savingNewValue: true});
        
        try {
            // Save all existing vendors that have been modified
            for (const vendor of this.state.vendors) {
                await vendorService.updateVendor(vendor);
            }
            
            // Get all new vendors that have been filled out
            const vendorsToAdd = this.state.pendingVendors
                .filter(line => line.name && line.name.trim())
                .map(line => ({
                    name: line.name.trim(),
                    website: line.website || '',
                    phoneNumber: line.phoneNumber || '',
                    address: line.address || '',
                    primaryContact: line.primaryContact || '',
                    primaryEmail: line.primaryEmail || '',
                    secondaryContact: line.secondaryContact || '',
                    secondaryEmail: line.secondaryEmail || '',
                    paymentTermsId: line.paymentTermsId || null
                }));
            
            // Add all new vendors
            const newVendors = await Promise.all(
                vendorsToAdd.map(vendorData => vendorService.createVendor(vendorData))
            );
            
            // Keep the current vendors state (already updated) and add the new ones
            self.setState(prevState => ({
                vendors: [...prevState.vendors, ...newVendors],
                savingNewValue: false,
                showVendorBulkEdit: false,
                pendingVendors: []
            }));
            
            const totalChanges = this.state.vendors.length + vendorsToAdd.length;
            window.$alertSuccess(`${totalChanges} vendor${totalChanges > 1 ? 's' : ''} saved successfully`);
        } catch (error) {
            console.error('Error saving vendors:', error);
            self.setState({savingNewValue: false});
            window.$alertError('Error saving vendors');
        }
    };

    cancelVendorBulkEdit = () => {
        this.setState({
            showVendorBulkEdit: false,
            pendingVendors: []
        });
    };

    // Payment Terms Bulk Edit Methods
    addPaymentTermsBulkLine = () => {
        const newLine = {
            id: uuid(),
            name: '',
            description: '',
            days: '',
            isComplete: false
        };
        
        this.setState(prevState => ({
            pendingPaymentTerms: [...prevState.pendingPaymentTerms, newLine]
        }));
    };

    handlePaymentTermsBulkChange = (lineId, field, value) => {
        this.setState(prevState => ({
            pendingPaymentTerms: prevState.pendingPaymentTerms.map(line => 
                line.id === lineId 
                    ? { ...line, [field]: value, isComplete: !!(line.name && line.name.trim()) }
                    : line
            )
        }));
    };

    removePaymentTermsBulkLine = (lineId) => {
        this.setState(prevState => ({
            pendingPaymentTerms: prevState.pendingPaymentTerms.filter(line => line.id !== lineId)
        }));
    };

    handlePaymentTermsBulkKeyDown = (lineId, event) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            // Get the current field name from the input element
            const currentField = event.target.name;
            
            // Define the field order for tabbing
            const fieldOrder = ['name', 'description', 'days'];
            const currentFieldIndex = fieldOrder.indexOf(currentField);
            
            // If we're on the last field, go to the name field of the next line (or create one)
            if (currentFieldIndex === fieldOrder.length - 1) {
                event.preventDefault();
                
                // Find current line index
                const currentLineIndex = this.state.pendingPaymentTerms.findIndex(line => line.id === lineId);
                
                // If there's a next line, focus its name field
                if (currentLineIndex < this.state.pendingPaymentTerms.length - 1) {
                    const nextLineId = this.state.pendingPaymentTerms[currentLineIndex + 1].id;
                    
                    setTimeout(() => {
                        const nameField = document.querySelector(`[data-payment-terms-line-id="${nextLineId}"] input[name="name"]`);
                        if (nameField) {
                            nameField.focus();
                            nameField.select();
                        }
                    }, 100);
                } else {
                    // No next line exists, create one and focus its name field
                    this.addPaymentTermsBulkLine();
                    
                    setTimeout(() => {
                        const newLineId = this.state.pendingPaymentTerms[this.state.pendingPaymentTerms.length - 1]?.id;
                        if (newLineId) {
                            const nameField = document.querySelector(`[data-payment-terms-line-id="${newLineId}"] input[name="name"]`);
                            if (nameField) {
                                nameField.focus();
                                nameField.select();
                            }
                        }
                    }, 100);
                }
            }
            // Otherwise, let the default tab behavior handle moving to the next field
        }
    };

    saveAllPaymentTermsBulkChanges = async () => {
        const self = this;
        const vendorService = ServiceLocator.Instance.getVendorService();
        
        self.setState({savingNewValue: true});
        
        try {
            // Save all existing payment terms that have been modified
            for (const paymentTerms of this.state.paymentTerms) {
                const paymentTermsToSave = {
                    ...paymentTerms,
                    days: parseNumberInputValue(paymentTerms.days)
                };
                await vendorService.updatePaymentTerms(paymentTermsToSave);
            }
            
            // Get all new payment terms that have been filled out
            const paymentTermsToAdd = this.state.pendingPaymentTerms
                .filter(line => line.name && line.name.trim())
                .map(line => ({
                    name: line.name.trim(),
                    description: line.description || '',
                    days: parseNumberInputValue(line.days) || 0,
                    isActive: true
                }));
            
            // Add all new payment terms
            const newPaymentTerms = await Promise.all(
                paymentTermsToAdd.map(paymentTermsData => vendorService.createPaymentTerms(paymentTermsData))
            );
            
            // Keep the current payment terms state (already updated) and add the new ones
            self.setState(prevState => ({
                paymentTerms: [...prevState.paymentTerms, ...newPaymentTerms],
                savingNewValue: false,
                showPaymentTermsBulkEdit: false,
                pendingPaymentTerms: []
            }));
            
            const totalChanges = this.state.paymentTerms.length + paymentTermsToAdd.length;
            window.$alertSuccess(`${totalChanges} payment term${totalChanges > 1 ? 's' : ''} saved successfully`);
        } catch (error) {
            console.error('Error saving payment terms:', error);
            self.setState({savingNewValue: false});
            window.$alertError('Error saving payment terms');
        }
    };

    cancelPaymentTermsBulkEdit = () => {
        this.setState({
            showPaymentTermsBulkEdit: false,
            pendingPaymentTerms: []
        });
    };

    getSortedAndFilteredVendors = () => {
        const { vendors, searchTerm, sortBy, sortDirection } = this.state;
        
        let filtered = vendors;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = vendors.filter(vendor => 
                vendor.name.toLowerCase().includes(term) ||
                (vendor.primaryContact && vendor.primaryContact.toLowerCase().includes(term)) ||
                (vendor.primaryEmail && vendor.primaryEmail.toLowerCase().includes(term)) ||
                (vendor.secondaryContact && vendor.secondaryContact.toLowerCase().includes(term)) ||
                (vendor.secondaryEmail && vendor.secondaryEmail.toLowerCase().includes(term))
            );
        }

        return filtered.sort((a, b) => {
            let aValue = a[sortBy] || '';
            let bValue = b[sortBy] || '';
            
            if (sortBy === 'paymentTerms') {
                aValue = a.paymentTerms?.name || '';
                bValue = b.paymentTerms?.name || '';
            }
            
            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            
            if (sortDirection === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });
    };

    render() {
        const { classes } = this.props;
        const {
            loading,
            vendors,
            paymentTerms,
            page,
            rowsPerPage,
            searchTerm,
            sortBy,
            sortDirection,
            editingVendor,
            newVendor,
            showAddDialog,
            editingPaymentTerms,
            newPaymentTerms,
            showPaymentTermsDialog,
            activeTab
        } = this.state;

        if (loading) {
            return (
                <DashboardTemplate title="Vendors" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress />
                    </div>
                </DashboardTemplate>
            );
        }

        const sortedVendors = this.getSortedAndFilteredVendors();
        const paginatedVendors = sortedVendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        const SortButton = ({ field, children }) => (
            <th 
                className={classes.sortableHeader}
                onClick={() => this.handleSort(field)}
            >
                {children}
                {sortBy === field && (
                    <FontAwesomeIcon 
                        icon={sortDirection === 'asc' ? faChevronUp : faChevronDown} 
                        size="sm"
                        style={{ marginLeft: '8px' }}
                    />
                )}
            </th>
        );

        return (
            <DashboardTemplate title="Vendors" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div className="container">
                        {activeTab === 0 && (
                            <>
                                <div className={`${classes.toolbar} toolbar-responsive`}>
                                    <div className={`${classes.toolbarLeft} toolbar-left`} style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, maxWidth: '40%' }}>
                                        <TextField
                                            value={searchTerm}
                                            onChange={this.searchVendor}
                                            label="Search Vendors..."
                                            variant="outlined"
                                            className="search-field"
                                            style={{minWidth: '300px'}}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
                                        <Button
                                            className="success-button-lg"
                                            variant="contained"
                                            color="primary"
                                            onClick={this.handleTabChange}
                                            style={{ minWidth: '200px' }}
                                        >
                                            Manage Payment Terms
                                        </Button>
                                    </div>
                                    <div className={`${classes.toolbarRight} toolbar-right`} style={{ flex: 1, maxWidth: '40%', justifyContent: 'flex-end' }}>
                                        
                                    </div>
                                </div>
                                <center>
                                    <TablePagination
                                        rowsPerPageOptions={[50, 100, 200, 500]}
                                        colSpan={10}
                                        count={sortedVendors.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        slotProps={{
                                            select: {
                                                'aria-label': 'rows per page',
                                            },
                                            actions: {
                                                showFirstButton: true,
                                                showLastButton: true,
                                                slots: {
                                                    firstPageIcon: FirstPageIcon,
                                                    lastPageIcon: LastPageIcon,
                                                    nextPageIcon: ChevronRightIcon,
                                                    backPageIcon: ChevronLeftIcon,
                                                },
                                            },
                                        }}
                                        onChangePage={this.handleChangePage}
                                        onChangeRowsPerPage={this.handleChangeRowsPerPage}
                                    />
                                </center>
                                <div className="parts-table-container">
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <SortButton field="name">Name</SortButton>
                                                <SortButton field="paymentTerms">Payment Terms</SortButton>
                                                <th>Website</th>
                                                <th>Phone</th>
                                                <th>Address</th>
                                                <th>Primary Contact</th>
                                                <th>Primary Email</th>
                                                <th>Secondary Contact</th>
                                                <th>Secondary Email</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!this.state.showVendorBulkEdit ? (
                                                // Normal view - show existing vendors
                                                paginatedVendors.map((vendor) => (
                                                    <tr key={vendor.id}>
                                                        <td>{vendor.name}</td>
                                                        <td>{vendor.paymentTerms?.name || ''}</td>
                                                        <td>
                                                            {vendor.website && vendor.website.length > 0 ? 
                                                                <a href={vendor.website} target="_blank" rel="noopener noreferrer">{vendor.website}</a> : 
                                                                ''
                                                            }
                                                        </td>
                                                        <td>{vendor.phoneNumber || ''}</td>
                                                        <td>{vendor.address || ''}</td>
                                                        <td>{vendor.primaryContact || ''}</td>
                                                        <td>{vendor.primaryEmail || ''}</td>
                                                        <td>{vendor.secondaryContact || ''}</td>
                                                        <td>{vendor.secondaryEmail || ''}</td>
                                                        <td>
                                                            <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                <Tooltip title="Delete">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="secondary"
                                                                        onClick={() => this.handleDeleteVendor(vendor)}
                                                                        tabIndex={-1}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                // Bulk edit view - show all vendors in edit mode plus new lines
                                                <>
                                                    {/* Existing vendors in edit mode */}
                                                    {paginatedVendors.map((vendor) => (
                                                        <tr key={vendor.id}>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.name}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'name', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <FormControl variant="outlined" size="small" className={classes.editableInput}>
                                                                    <Select
                                                                        value={vendor.paymentTermsId || ''}
                                                                        onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'paymentTermsId', e.target.value)}
                                                                    >
                                                                        <MenuItem value="">
                                                                            <em>None</em>
                                                                        </MenuItem>
                                                                        {paymentTerms.map((pt) => (
                                                                            <MenuItem key={pt.id} value={pt.id}>
                                                                                {pt.name}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.website || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'website', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.phoneNumber || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'phoneNumber', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.address || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'address', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.primaryContact || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'primaryContact', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.primaryEmail || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'primaryEmail', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.secondaryContact || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'secondaryContact', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.secondaryEmail || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'secondaryEmail', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                    <Tooltip title="Delete">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={() => this.handleDeleteVendor(vendor)}
                                                                            tabIndex={-1}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    
                                                    {/* New vendor lines */}
                                                    {this.state.pendingVendors.map((line, index) => (
                                                        <tr key={line.id} data-vendor-line-id={line.id} style={{backgroundColor: '#f5f5f5'}}>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="name"
                                                                    value={line.name}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'name', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    autoFocus={index === this.state.pendingVendors.length - 1}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <FormControl variant="outlined" size="small" className={classes.editableInput}>
                                                                    <Select
                                                                        name="paymentTermsId"
                                                                        value={line.paymentTermsId || ''}
                                                                        onChange={(e) => this.handleVendorBulkChange(line.id, 'paymentTermsId', e.target.value)}
                                                                        onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    >
                                                                        <MenuItem value="">
                                                                            <em>None</em>
                                                                        </MenuItem>
                                                                        {paymentTerms.map((pt) => (
                                                                            <MenuItem key={pt.id} value={pt.id}>
                                                                                {pt.name}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="website"
                                                                    value={line.website}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'website', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="phoneNumber"
                                                                    value={line.phoneNumber}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'phoneNumber', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="address"
                                                                    value={line.address}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'address', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="primaryContact"
                                                                    value={line.primaryContact}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'primaryContact', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="primaryEmail"
                                                                    value={line.primaryEmail}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'primaryEmail', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="secondaryContact"
                                                                    value={line.secondaryContact}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'secondaryContact', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="secondaryEmail"
                                                                    value={line.secondaryEmail}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'secondaryEmail', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                    <Tooltip title="Remove Line">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={() => this.removeVendorBulkLine(line.id)}
                                                                            tabIndex={-1}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                
                                {/* Edit/Save/Cancel buttons below table */}
                                <div style={{marginTop: 20, textAlign: 'center'}}>
                                    {!this.state.showVendorBulkEdit ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                                this.setState({
                                                    showVendorBulkEdit: true,
                                                    pendingVendors: [{ id: uuid(), name: '', website: '', phoneNumber: '', address: '', primaryContact: '', primaryEmail: '', secondaryContact: '', secondaryEmail: '', paymentTermsId: null, isComplete: false }]
                                                });
                                                // Focus the first name field after a brief delay
                                                setTimeout(() => {
                                                    const nameField = document.querySelector('[data-vendor-line-id] input[name="name"]');
                                                    if (nameField) {
                                                        nameField.focus();
                                                    }
                                                }, 100);
                                            }}
                                            className="success-button-lg action-button"
                                            style={{minWidth: 100}}
                                        >
                                            Edit
                                        </Button>
                                    ) : (
                                        this.state.savingNewValue ? (
                                            <CircularProgress />
                                        ) : (
                                            <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                                                <Button 
                                                    onClick={this.saveAllVendorBulkChanges}
                                                    variant="contained"
                                                    color="primary"
                                                    className="success-button-lg"
                                                    disabled={false}
                                                >
                                                    Save All Changes
                                                </Button>
                                                <Button 
                                                    onClick={this.cancelVendorBulkEdit}
                                                    variant="contained"
                                                    className="cancel-button-lg"
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        )}

                        {activeTab === 1 && (
                            <>
                                <div className={`${classes.toolbar} toolbar-responsive`} style={{ marginBottom: '20px' }}>
                                    <div className={`${classes.toolbarLeft} toolbar-left`} style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, maxWidth: '40%' }}>
                                        
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
                                        <Button
                                            className="success-button-lg"
                                            variant="contained"
                                            color="primary"
                                            onClick={this.handleTabChange}
                                            style={{ minWidth: '200px' }}
                                        >
                                            Manage Vendors
                                        </Button>
                                    </div>
                                    <div className={`${classes.toolbarRight} toolbar-right`} style={{ flex: 1, maxWidth: '40%', justifyContent: 'flex-end' }}>
                                        
                                    </div>
                                </div>
                                <div className="parts-table-container" style={{maxWidth: 1200, textAlign: 'center'}}>
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Days</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!this.state.showPaymentTermsBulkEdit ? (
                                                // Normal view - show existing payment terms
                                                paymentTerms.map((pt) => (
                                                    <tr key={pt.id}>
                                                        <td>{pt.name}</td>
                                                        <td>{pt.description || ''}</td>
                                                        <td>{pt.days}</td>
                                                        <td>
                                                            <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                <Tooltip title="Deactivate">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="secondary"
                                                                        onClick={() => this.handleDeletePaymentTerms(pt)}
                                                                        tabIndex={-1}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                // Bulk edit view - show all payment terms in edit mode plus new lines
                                                <>
                                                    {/* Existing payment terms in edit mode */}
                                                    {paymentTerms.map((pt) => (
                                                        <tr key={pt.id}>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={pt.name}
                                                                    onChange={(e) => this.handlePaymentTermsBulkFieldChange(pt.id, 'name', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={pt.description || ''}
                                                                    onChange={(e) => this.handlePaymentTermsBulkFieldChange(pt.id, 'description', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={getNumberInputDisplayValue(pt.days)}
                                                                    onChange={(e) => this.handlePaymentTermsBulkFieldChange(pt.id, 'days', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    type="number"
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                    <Tooltip title="Deactivate">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={() => this.handleDeletePaymentTerms(pt)}
                                                                            tabIndex={-1}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    
                                                    {/* New payment terms lines */}
                                                    {this.state.pendingPaymentTerms.map((line, index) => (
                                                        <tr key={line.id} data-payment-terms-line-id={line.id} style={{backgroundColor: '#f5f5f5'}}>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="name"
                                                                    value={line.name}
                                                                    onChange={(e) => this.handlePaymentTermsBulkChange(line.id, 'name', e.target.value)}
                                                                    onKeyDown={(e) => this.handlePaymentTermsBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    autoFocus={index === this.state.pendingPaymentTerms.length - 1}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="description"
                                                                    value={line.description}
                                                                    onChange={(e) => this.handlePaymentTermsBulkChange(line.id, 'description', e.target.value)}
                                                                    onKeyDown={(e) => this.handlePaymentTermsBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="days"
                                                                    value={getNumberInputDisplayValue(line.days)}
                                                                    onChange={(e) => this.handlePaymentTermsBulkChange(line.id, 'days', e.target.value)}
                                                                    onKeyDown={(e) => this.handlePaymentTermsBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    type="number"
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                    <Tooltip title="Remove Line">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={() => this.removePaymentTermsBulkLine(line.id)}
                                                                            tabIndex={-1}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                
                                {/* Edit/Save/Cancel buttons below table */}
                                <div style={{marginTop: 20, textAlign: 'center'}}>
                                    {!this.state.showPaymentTermsBulkEdit ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                                this.setState({
                                                    showPaymentTermsBulkEdit: true,
                                                    pendingPaymentTerms: [{ id: uuid(), name: '', description: '', days: '', isComplete: false }]
                                                });
                                                // Focus the first name field after a brief delay
                                                setTimeout(() => {
                                                    const nameField = document.querySelector('[data-payment-terms-line-id] input[name="name"]');
                                                    if (nameField) {
                                                        nameField.focus();
                                                    }
                                                }, 100);
                                            }}
                                            className="success-button-lg action-button"
                                            style={{minWidth: 100}}
                                        >
                                            Edit
                                        </Button>
                                    ) : (
                                        this.state.savingNewValue ? (
                                            <CircularProgress />
                                        ) : (
                                            <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                                                <Button 
                                                    onClick={this.saveAllPaymentTermsBulkChanges}
                                                    variant="contained"
                                                    color="primary"
                                                    className="success-button-lg"
                                                    disabled={false}
                                                >
                                                    Save All Changes
                                                </Button>
                                                <Button 
                                                    onClick={this.cancelPaymentTermsBulkEdit}
                                                    variant="contained"
                                                    className="cancel-button-lg"
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        )}

                    </div>
                </div>
            </DashboardTemplate>
        );
    }
}

let globalProps;

Vendors.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Vendors);